// ─────────────────────────────────────────────────────────────────────────────
// useAgentData — custom hook
// Fetches data from whichever AI personality is active.
// Polls the activity feed every 8 seconds so you can watch the agent work live.
// Personality-agnostic — pass in any personality config and it just works.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback, useRef } from "react";

const BASE_URL = process.env.REACT_APP_BACKEND_URL ?? "";
const ACTIVITY_POLL_MS = 8000; // refresh activity log every 8 seconds
const DATA_POLL_MS = 60000; // the opportunity corpus is large; refresh it once a minute
const REQUEST_TIMEOUT_MS = 20000;

async function fetchWithTimeout(url, options = {}) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeout);
  }
}

export default function useAgentData(personality, authToken = null, onAuthExpired = null, extraOpportunityParams = '') {
  const [opportunities, setOpportunities] = useState([]);
  const [activity, setActivity] = useState([]);
  const [agentStatus, setAgentStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [triggering, setTriggering] = useState(false);

  const activityInterval = useRef(null);
  const dataInterval = useRef(null);

  // ── FETCH OPPORTUNITIES ──
  const fetchOpportunities = useCallback(async () => {
    if (!personality || !authToken) return;
    try {
      const headers = { Authorization: `Bearer ${authToken}` };
      const sep = personality.data_endpoint.includes('?') ? '&' : '?';
      const url = BASE_URL + personality.data_endpoint + (extraOpportunityParams ? sep + extraOpportunityParams : '');
      const res = await fetchWithTimeout(url, { headers });

      // Token rejected — bounce to login so the user can recover.
      if (res.status === 401) {
        if (onAuthExpired) onAuthExpired();
        else setError('Session expired — please log in again');
        return;
      }

      if (!res.ok) {
        setError(`Failed to load opportunities (HTTP ${res.status})`);
        return;
      }

      const data = await res.json();
      // API returns array directly, not wrapped in {opportunities: [...]}
      const opps = Array.isArray(data) ? data : (data.opportunities || []);
      setOpportunities(opps);
      setError(null);
    } catch (err) {
      console.error('[useAgentData] Fetch error:', err);
      setError(err?.name === 'AbortError'
        ? 'Opportunity data took too long to load — retrying automatically.'
        : 'Could not reach the server — check your connection.');
    }
  }, [personality, authToken, onAuthExpired, extraOpportunityParams]);

  // ── FETCH ACTIVITY LOG ──
  const fetchActivity = useCallback(async () => {
    if (!personality || !authToken) return;
    try {
      const headers = { Authorization: `Bearer ${authToken}` };
      const res = await fetchWithTimeout(BASE_URL + personality.activity_endpoint + "?limit=60", { headers });
      if (res.status === 401) { if (onAuthExpired) onAuthExpired(); return; }
      if (!res.ok) return;
      const data = await res.json();
      // API returns array directly, not wrapped in {log: [...]}
      const activities = Array.isArray(data) ? data : (data.log || []);
      setActivity(activities);
    } catch (err) {
      // Activity is non-critical — don't break the app if it fails
    }
  }, [personality, authToken, onAuthExpired]);

  // ── FETCH AGENT STATUS ──
  const fetchStatus = useCallback(async () => {
    if (!personality || !authToken) return;
    try {
      const headers = { Authorization: `Bearer ${authToken}` };
      const res = await fetchWithTimeout(BASE_URL + personality.status_endpoint, { headers });
      if (res.status === 401) { if (onAuthExpired) onAuthExpired(); return; }
      if (!res.ok) return;
      const data = await res.json();
      setAgentStatus(data);
    } catch (err) {}
  }, [personality, authToken, onAuthExpired]);

  // ── FETCH STATS ──
  const fetchStats = useCallback(async () => {
    if (!authToken) return;
    try {
      const headers = { Authorization: `Bearer ${authToken}` };
      const res = await fetchWithTimeout(BASE_URL + "/api/stats", { headers });
      if (res.status === 401) { if (onAuthExpired) onAuthExpired(); return; }
      if (!res.ok) return;
      const data = await res.json();
      setStats(data);
    } catch (err) {}
  }, [authToken, onAuthExpired]);

  // ── TRIGGER MANUAL RUN ──
  const triggerRun = useCallback(async () => {
    if (!personality || !authToken) return;
    setTriggering(true);
    try {
      const headers = { Authorization: `Bearer ${authToken}` };
      await fetch(BASE_URL + personality.trigger_endpoint, { method: "POST", headers });
      setLastRefresh(new Date());
      // Re-fetch activity after a short delay so we catch the new log entries
      setTimeout(() => {
        fetchActivity();
        fetchStatus();
        setTriggering(false);
      }, 3000);
    } catch (err) {
      setError("Failed to trigger run");
      setTriggering(false);
    }
  }, [personality, authToken, fetchActivity, fetchStatus]);

  // ── FULL REFRESH ──
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([fetchOpportunities(), fetchActivity(), fetchStatus(), fetchStats()]);
      setLastRefresh(new Date());
    } finally {
      setLoading(false);
    }
  }, [fetchOpportunities, fetchActivity, fetchStatus, fetchStats]);

  // ── ON MOUNT / PERSONALITY CHANGE ──
  useEffect(() => {
    if (!personality || !authToken) return;

    refresh();

    // Activity stays live without repeatedly downloading the full dashboard.
    activityInterval.current = setInterval(() => {
      fetchActivity();
      fetchStatus();
    }, ACTIVITY_POLL_MS);

    dataInterval.current = setInterval(() => {
      fetchOpportunities();
      fetchStats();
    }, DATA_POLL_MS);

    return () => {
      if (activityInterval.current) clearInterval(activityInterval.current);
      if (dataInterval.current) clearInterval(dataInterval.current);
    };
  }, [personality, authToken, refresh, fetchActivity, fetchStatus, fetchOpportunities, fetchStats]);

  return {
    opportunities,
    activity,
    agentStatus,
    stats,
    loading,
    error,
    lastRefresh,
    refresh,
    triggerRun,
    triggering
  };
}
